import { useAccountantOwnerWork, useAnswerOwnerOpenItem, useRecordOwnerAttestation } from '@/api/hooks/useAccountantOwnerWork';
import { AccountantChangeDigest } from './changes/AccountantChangeDigest';
import { AttestationForm } from './open-items/owner/AttestationForm';
import { EvidenceUploader } from './open-items/owner/EvidenceUploader';
import { OpenItemAnswerCard } from './open-items/owner/OpenItemAnswerCard';

type OwnerOpenItem = {
  open_item_id: string;
  title: string;
  message: string;
  kind: string;
  status: 'sent' | 'answered' | 'resolved' | 'reopened';
  version: number;
  answer_text?: string | null;
};

const attestationKindByItem: Record<string, string | undefined> = {
  coverage_gap: 'coverage_complete',
  ambiguous_deposit: 'cash_income',
  missing_1099: 'cash_income',
  missing_receipt: 'no_receipt',
  mileage_log: 'mileage',
  home_office: 'home_office',
};

export function AccountantWorkPanel({ taxYear }: { taxYear: number }) {
  const query = useAccountantOwnerWork(taxYear);
  const answer = useAnswerOwnerOpenItem(taxYear);
  const attestation = useRecordOwnerAttestation(taxYear);
  if (query.isLoading) return <section aria-label="Loading accountant work" className="rounded-[10px] border border-border bg-card p-4"><div className="h-6 w-56 animate-pulse rounded bg-muted" /><div className="mt-3 h-16 animate-pulse rounded bg-muted" /></section>;
  if (query.isError || !query.data) return <section className="rounded-[10px] border border-border bg-card p-4"><h2 className="font-semibold">Accountant work</h2><p role="alert" className="mt-2 text-sm text-muted-foreground">Accountant work is unavailable.</p><button type="button" onClick={() => void query.refetch()} className="mt-3 min-h-11 rounded-md border px-3 text-sm">Retry</button></section>;

  const { open_items: openItems, accountant_changes: changes, year_lock: yearLock } = query.data;
  const answerCount = openItems.items.filter((item) => item.status === 'sent' || item.status === 'reopened').length;
  if (openItems.items.length === 0 && changes.items.length === 0) return null;
  return (
    <section className="mt-4 grid gap-4 desktop:grid-cols-[minmax(0,1fr)_400px]" aria-label="Accountant work">
      <div className="space-y-4">
        {openItems.items.length > 0 && (
          <section className="rounded-[10px] border border-border bg-card p-4" aria-labelledby="accountant-open-items-heading">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 id="accountant-open-items-heading" className="font-semibold">From your accountant · {answerCount} to answer</h2>
              {yearLock.locked && <span className="rounded-full bg-foreground px-3 py-1 text-xs text-background">Filed · locked</span>}
            </div>
            <div className="mt-3 divide-y divide-border">
              {openItems.items.map((rawItem) => {
                const item = rawItem as OwnerOpenItem;
                const openItemId = String(item.open_item_id);
                const canCollaborate = !yearLock.locked && item.status !== 'resolved';
                const canAnswer = !yearLock.locked && (item.status === 'sent' || item.status === 'reopened');
                const attestationKind = attestationKindByItem[item.kind];
                return (
                <article key={openItemId} className="py-3 first:pt-0 last:pb-0">
                  <h3 className="font-medium">{String(item.title)}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{String(item.message)}</p>
                  {item.answer_text != null && <p className="mt-2 text-sm">{String(item.answer_text)}</p>}
                  {item.status === 'answered' && <p className="mt-2 text-xs font-semibold text-muted-foreground">Answered</p>}
                  {item.status === 'resolved' && <p className="mt-2 text-xs font-semibold text-muted-foreground">Resolved</p>}
                  {canAnswer && <div className="mt-3"><OpenItemAnswerCard openItemId={openItemId} expectedVersion={item.version} onAnswer={(input) => answer.mutate(input)} /></div>}
                  {canCollaborate && <div className="mt-3"><EvidenceUploader taxYear={taxYear} openItemId={openItemId} /></div>}
                  {canCollaborate && attestationKind && <div className="mt-3"><AttestationForm onAccept={(statement) => attestation.mutate({ openItemId, kind: attestationKind, statementText: statement })} /></div>}
                </article>
                );
              })}
            </div>
          </section>
        )}
        {yearLock.locked && answerCount > 0 && <p className="text-sm text-muted-foreground">This year is read-only after filing.</p>}
      </div>
      <AccountantChangeDigest changes={changes.items as Array<Record<string, unknown>>} />
      <p className="text-xs text-muted-foreground desktop:col-span-2">{query.data.retention_notice}</p>
    </section>
  );
}

export default AccountantWorkPanel;
